import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, object = authed, false = anon
  const [theme, setTheme] = useState(localStorage.getItem("cc-theme") === "light" ? "light" : "dark");

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback handles it
    if (!localStorage.getItem("cc-token")) {
      setUser(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("cc-token", data.token);
    setUser(data.user);
    return data.user;
  };

  const completeGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    localStorage.setItem("cc-token", data.token);
    setUser(data.user);
    return data.user;
  };

  const setPassword = async (newPassword, currentPassword) => {
    await api.post("/auth/set-password", { new_password: newPassword, current_password: currentPassword || null });
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("cc-token");
    setUser(false);
  };

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("cc-theme", next);
    document.documentElement.classList.toggle("dark", next === "dark");
    document.documentElement.classList.toggle("light", next === "light");
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, completeGoogleSession, setPassword, checkAuth, theme, toggleTheme }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
